import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SEO } from '../components/SEO';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  { value: 'publish', label: 'Quiero publicar mis eventos' },
  { value: 'publish_others', label: 'Quiero publicar eventos de otros' },
  { value: 'ambassador', label: 'Quiero ser embajador PlanCity en mi ciudad' },
  { value: 'support', label: 'Necesito soporte técnico' },
  { value: 'other', label: 'Otro' },
];

const initialState = { name: '', email: '', phone: '', subject: 'publish', customSubject: '', message: '' };

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Ingresa tu nombre';
    if (!form.email.trim()) e.email = 'Ingresa tu email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email inválido';
    if (form.phone && !/^[\d+\-\s()]{6,30}$/.test(form.phone.trim())) e.phone = 'Teléfono inválido';
    if (form.subject === 'other' && form.customSubject.trim().length < 3) {
      e.customSubject = 'Especifica el asunto (mínimo 3 caracteres)';
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      e.message = 'El mensaje debe tener al menos 10 caracteres';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const subjectPayload = form.subject === 'other'
        ? form.customSubject.trim().slice(0, 50)
        : form.subject;
      await axios.post(`${API_URL}/api/platform-messages`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: subjectPayload,
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm(initialState);
      toast.success('Mensaje enviado. Te responderemos pronto.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo enviar el mensaje. Inténtalo nuevamente.';
      toast.error(typeof detail === 'string' ? detail : 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-neutral-200" data-testid="contact-page">
      <SEO
        path="/contacto"
        title="Contacto"
        description="Escríbenos a PlanCity. Responderemos tu consulta general, soporte técnico o atención a proveedores."
      />
      <div className="relative">
        {/* Ambient glow */}
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.5) 0%, rgba(0,204,102,0) 70%)' }}
          aria-hidden="true"
        />

        <div className="relative max-w-3xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-[#00CC66] transition-colors mb-8"
            data-testid="contact-back-home"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <header className="mb-10">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white">
              Hablemos
            </h1>
            <p className="mt-4 text-base text-neutral-400 max-w-xl">
              ¿Tienes una consulta, quieres publicar tus eventos o necesitas soporte? Escríbenos y te responderemos a la brevedad.
            </p>
          </header>

          {submitted ? (
            <div
              className="rounded-2xl border border-[#00CC66]/30 bg-[#00CC66]/5 p-8 text-center"
              data-testid="contact-success"
            >
              <CheckCircle2 className="w-12 h-12 mx-auto text-[#00CC66] mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">¡Mensaje enviado!</h2>
              <p className="text-neutral-400 mb-6">
                Gracias por escribirnos. Te responderemos cuanto antes.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="border-[#00CC66]/40 text-[#00CC66] hover:bg-[#00CC66]/10 rounded-full"
                data-testid="contact-send-another"
              >
                Enviar otro mensaje
              </Button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="space-y-5 rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="contact-name" className="text-neutral-300 text-sm">
                    Nombre <span className="text-[#00CC66]">*</span>
                  </Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-[#00CC66]"
                    placeholder="Tu nombre completo"
                    maxLength={255}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="contact-email" className="text-neutral-300 text-sm">
                    Email <span className="text-[#00CC66]">*</span>
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-[#00CC66]"
                    placeholder="tu@email.com"
                    maxLength={255}
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="contact-phone" className="text-neutral-300 text-sm">
                    Teléfono <span className="text-neutral-500 text-xs">(opcional)</span>
                  </Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-[#00CC66]"
                    placeholder="+56 9 1234 5678"
                    maxLength={30}
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-400">{errors.phone}</p>}
                </div>

                <div>
                  <Label htmlFor="contact-subject" className="text-neutral-300 text-sm">
                    Asunto <span className="text-[#00CC66]">*</span>
                  </Label>
                  <Select
                    value={form.subject}
                    onValueChange={(v) => setField('subject', v)}
                  >
                    <SelectTrigger
                      id="contact-subject"
                      data-testid="contact-subject-select"
                      className="mt-1.5 bg-white/5 border-white/10 text-white focus:ring-[#00CC66]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s.value} value={s.value} data-testid={`subject-${s.value}`}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.subject === 'other' && (
                <div data-testid="contact-custom-subject-wrapper">
                  <Label htmlFor="contact-custom-subject" className="text-neutral-300 text-sm">
                    Asunto personalizado <span className="text-[#00CC66]">*</span>
                  </Label>
                  <Input
                    id="contact-custom-subject"
                    data-testid="contact-custom-subject-input"
                    value={form.customSubject}
                    onChange={(e) => setField('customSubject', e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-[#00CC66]"
                    placeholder="Cuéntanos brevemente sobre qué nos contactas"
                    maxLength={50}
                  />
                  <div className="mt-1 flex items-center justify-between">
                    {errors.customSubject ? (
                      <p className="text-xs text-red-400">{errors.customSubject}</p>
                    ) : (
                      <span className="text-xs text-neutral-500">Máximo 50 caracteres</span>
                    )}
                    <span className="text-xs text-neutral-500">{form.customSubject.length}/50</span>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="contact-message" className="text-neutral-300 text-sm">
                  Mensaje <span className="text-[#00CC66]">*</span>
                </Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => setField('message', e.target.value)}
                  className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-[#00CC66] min-h-[160px] resize-y"
                  placeholder="Cuéntanos en qué podemos ayudarte..."
                  maxLength={5000}
                />
                <div className="mt-1 flex items-center justify-between">
                  {errors.message ? (
                    <p className="text-xs text-red-400">{errors.message}</p>
                  ) : (
                    <span className="text-xs text-neutral-500">Mínimo 10 caracteres</span>
                  )}
                  <span className="text-xs text-neutral-500">{form.message.length}/5000</span>
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-neutral-500">
                  Al enviar aceptas nuestra{' '}
                  <Link to="/privacidad" className="text-[#00CC66] hover:underline">
                    Política de Privacidad
                  </Link>.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#00CC66] hover:bg-[#00b85b] text-[#04331e] font-semibold rounded-full px-6 inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Enviando…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" /> Enviar mensaje
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
